import React, { MouseEvent, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { ProductSequenceData } from "../store/product-list/ProductListTypes";
import ProductActions from "./ProductActions";
import ProductTileContent from "./product-title-content/ProductTitleContent";
import {
  selectProductAnalyticsData,
  selectProductFetched,
} from "../store/product/ProductSelectors";
import AppState from "../store/AppState";
import { selectOverlay } from "../store/overlay/OverlaySelectors";
import { PLATFORM_DEFAULTS } from "../utils/DefaultConfigs";
import { selectProductAttribute } from "../store/category/CategorySelectors";
import { selectConfigValue } from "../store/app-config/AppConfigSelectors";
import { AdapterInfoObject } from "../utils/AppConfigUtils";
import { selectAdapterInfo } from "../store/adapter-config/AdapterConfigSelectors";
import { selectSocialAnalyticsForProducts } from "../store/product-social-analytics/SocialAnalyticsSelectors";
import { OVERLAY_TABS } from "utils/Constants";
import { selectProductClusterListFetched } from "store/ai-cluster/AiClusterSelectors";
import {
  selectStoredPinnedProductIds,
  selectStoredUnpinnedProductIds,
} from "store/product-list/ProductListSelectors";
import { selectPinnedProductIds } from "store/product-list/ProductListSelectors";
import { isPinnedProduct } from "../utils/ProductUtil";
import { Theme, useTheme } from "@mui/material";

const StyledProductTile = styled.div<{
  selectedOverlay: string;
  inventoryBorderColor: string | null;
  isMultiValueAttribute: boolean;
  productView: boolean;
  typeCode: string | undefined;
  isPinnedProduct: boolean;
  isDraft: boolean;
  isArchived: boolean;
  isUnlisted: boolean;
  isPublished: boolean;
  theme: Theme;
}>`
  display: flex;
  flex-direction: column;
  align-content: ${({ typeCode }) =>
    typeCode === "ProductBundle" || typeCode === "ProductSet" ? "center" : ""};
  height: 100%;
  padding: 0.8em;
  box-shadow: ${({ isPinnedProduct }) =>
    isPinnedProduct
      ? "none"
      : "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)"};
  border-radius: 4px;
  background-color: ${({ isDraft, isArchived, isUnlisted, isPublished, theme }) =>
    isDraft || isArchived || isUnlisted || !isPublished
      ? `${theme.palette.neutral.lightGrey}`
      : `${theme.palette.tableHeaderRow.contrastText}`};
  border-top: ${({ inventoryBorderColor, isPinnedProduct }) =>
    inventoryBorderColor
      ? "13px solid " + inventoryBorderColor + "!important"
      : isPinnedProduct
        ? "5.5px dotted rgb(220, 220, 220) !important"
        : "none"};
  border-left: ${({ isPinnedProduct }) =>
    isPinnedProduct ? "5.5px dotted rgb(220, 220, 220) !important" : "none"};
  border-right: ${({ isPinnedProduct }) =>
    isPinnedProduct ? "5.5px dotted rgb(220, 220, 220) !important" : "none"};
  border-bottom: ${({ isPinnedProduct }) =>
    isPinnedProduct ? "5.5px dotted rgb(220, 220, 220) !important" : "none"};
  padding-top: ${({ inventoryBorderColor }) =>
    inventoryBorderColor ? "5px" : "15px"};
`;

const StyledProductSelectedOverlay = styled.div<{ elevated: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0.2 !important;
  background-color: #599dbb;
  margin: 5px;
  z-index: ${({ elevated }) => (elevated ? 100 : 10)};
`;

interface Props {
  product: ProductSequenceData;
  index: number;
  onSelectionChange: (
    itemId: string,
    cmdKeyActive: boolean,
    shiftKeyActive: boolean,
  ) => void;
  isSelected: boolean;
  hasProductsSelectedFromList: boolean;
  OnQuickMoveCompleteAction: (productId: string, moveTo: number) => void;
  productView: boolean;
  pageStart?: number;
  searchText?: string;
  currentProductId?: string;
}

export interface DragItem {
  draggedItem: ProductSequenceData;
  items: ProductSequenceData[];
  itemsDragStack: ProductSequenceData[];
  type: string;
  clientWidth: number;
  clientHeight: number;
}

const ProductTile = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    product,
    index,
    onSelectionChange,
    isSelected,
    hasProductsSelectedFromList,
    OnQuickMoveCompleteAction,
    productView,
    pageStart,
    searchText,
    currentProductId,
  } = props;
  const theme = useTheme();
  let inventoryBorderColor: string | null = null;
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const ecomPlatformType = adapterInfo?.adapterId?.toUpperCase() ?? "";
  const platformDefaults = PLATFORM_DEFAULTS[ecomPlatformType];
  const storeAndAccountInventory = useSelector((state: AppState) =>
    selectConfigValue(state, "inventoryThreshold"),
  );
  const inventoryThreshold = parseInt(
    storeAndAccountInventory !== ""
      ? storeAndAccountInventory
      : (platformDefaults?.inventoryThreshold ?? ""),
  );

  const fetchedProduct = useSelector((state: AppState) =>
    selectProductFetched(state, product.productId),
  );
  const isDraft = fetchedProduct?.isDraft ?? false;
  const isArchived = fetchedProduct?.isArchived ?? false;
  const isUnlisted = fetchedProduct?.isUnlisted ?? false;
  const isPublished = fetchedProduct?.isPublished ?? false;
  const socialAnalytics = useSelector(selectSocialAnalyticsForProducts);
  const productSimilarity = useSelector(selectProductClusterListFetched);
  const productIndex = fetchedProduct?.productId ?? "";
  const rating = socialAnalytics[productIndex]?.rating ?? -1;
  const productData = productSimilarity.find(
    (e) => e.productId === productIndex,
  ) || { cluster_score: -1, rank: "", score: -1 };
  const { cluster_score, rank, score: productScore } = productData;
  const score =
    productScore !== undefined && productScore > cluster_score
      ? productScore
      : cluster_score;
  const reviewCount = socialAnalytics[productIndex]?.reviewCount ?? -1;
  const productInventory = fetchedProduct?.stock;

  if (productInventory === 0) inventoryBorderColor = "#D50000";
  else if (productInventory && inventoryThreshold) {
    if (productInventory <= inventoryThreshold)
      inventoryBorderColor = "#F8A510";
    else inventoryBorderColor = null;
  }

  const selectedOverlay = useSelector(selectOverlay);
  const attribute = useSelector(selectProductAttribute);

  const showSingleProductAnalytics = useSelector((state: AppState) =>
    selectProductAnalyticsData(state, product.productId),
  )?.showAnalytics;

  const [isAnalyticsDisplayed, setIsAnalyticsDisplayed] =
    useState<boolean>(false);
  const [hover, setHover] = useState(false);

  const selectionHandler = (e: MouseEvent<HTMLDivElement>) => {
    if (!e.isDefaultPrevented())
      onSelectionChange(product.productId, e.metaKey || e.ctrlKey, e.shiftKey);
  };

  const mouseOverHandler = () => {
    if (productView) setHover(true);
  };
  const mouseOutHandler = () => {
    if (productView) setHover(false);
  };
  const pinnedProductIdsFromDB = useSelector(selectPinnedProductIds);
  const storedPinnedProductIds = useSelector(selectStoredPinnedProductIds);
  const storedUnpinnedProductIds = useSelector(selectStoredUnpinnedProductIds);
  const isProductPinned = useMemo(() => {
    return isPinnedProduct(
      storedPinnedProductIds,
      storedUnpinnedProductIds,
      pinnedProductIdsFromDB,
      product.productId,
    );
  }, [
    storedPinnedProductIds,
    storedUnpinnedProductIds,
    pinnedProductIdsFromDB,
    product.productId,
  ]);

  useEffect(() => {
    if (
      selectedOverlay === OVERLAY_TABS.ANALYTICS_OVERLAY &&
      showSingleProductAnalytics === undefined
    ) {
      setIsAnalyticsDisplayed(true);
    } else if (!showSingleProductAnalytics) {
      setIsAnalyticsDisplayed(false);
    }
  }, [showSingleProductAnalytics, selectedOverlay]);

  return (
    fetchedProduct && (
      <StyledProductTile
        onClick={selectionHandler}
        onMouseEnter={mouseOverHandler}
        onMouseLeave={mouseOutHandler}
        ref={ref}
        selectedOverlay={selectedOverlay}
        inventoryBorderColor={inventoryBorderColor}
        isMultiValueAttribute={attribute ? attribute.isMultiValue : false}
        productView={productView}
        typeCode={fetchedProduct.typeCode}
        isPinnedProduct={isProductPinned}
        isDraft={isDraft}
        isArchived={isArchived}
        isUnlisted={isUnlisted}
        isPublished={isPublished}
        theme={theme}
      >
        {isSelected && (
          <StyledProductSelectedOverlay
            elevated={
              selectedOverlay === OVERLAY_TABS.CATEGORIES_OVERLAY ||
              selectedOverlay === OVERLAY_TABS.ANALYTICS_OVERLAY ||
              selectedOverlay === OVERLAY_TABS.PRODUCTS_BADGES
            }
          />
        )}
        <ProductTileContent
          index={index}
          rating={rating}
          reviewCount={reviewCount}
          score={score}
          rank={rank}
          product={fetchedProduct}
          sequence={product.sequence}
          productView={productView}
          isDragging={false}
          isRecentlyAdded={
            product.hasOwnProperty("isRecentlyAdded") ? true : false
          }
          pageStart={pageStart}
          searchText={searchText}
          currentProductId={currentProductId}
        />
        {productView && hover && (
          <ProductActions
            productId={product.productId}
            position={index}
            isSelected={isSelected}
            hasProductsSelectedFromList={hasProductsSelectedFromList}
            onQuickMoveCompleteAction={OnQuickMoveCompleteAction}
            setIsAnalyticsDisplayed={setIsAnalyticsDisplayed}
            isAnalyticsDisplayed={isAnalyticsDisplayed}
            pageStart={pageStart}
          />
        )}
      </StyledProductTile>
    )
  );
});

export default React.memo(ProductTile);
